import { apiErrorMessage } from '@agos/api-client'
import {
  getGetLoyaltySettingsQueryKey,
  useGetLoyaltySettings,
  usePostSettingsLoyaltyTestEmail,
  usePutLoyaltySettings,
} from '@agos/api-client/customers'
import { T, tt, useAuth } from '@agos/shared'
import {
  ConfirmDialog,
  PageHeader,
  ReadFailedBlock,
  Spinner,
  cn,
  useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Heart,
  Info,
  KeyRound,
  Mail,
  Receipt,
  Save,
  Send,
  Star,
} from 'lucide-react'
import { useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import { EmailMessageEditor } from './EmailMessageEditor'
import {
  carriesCredential,
  channelsOf,
  emailProblem,
  formOf,
  mayTest,
  placeholderExample,
  sendingMessage,
  sendingState,
  senderPayload,
  templatePayload,
  templateProblemMessage,
  templateProblems,
  emptyMessageProblem,
  type EmailForm,
  type TemplateKind,
} from './email-settings'

/**
 * `/settings-email` — the sender, the master switch, and three templates.
 *
 * **FB-138 (security).** Legacy's page reads `GET /api/settings/loyalty`, which carries the live
 * `resendApiKey` and, on legacy, has **no authorization**: `x-franchise-id: 1` and no token answers 200 with
 * the key, as does a cook's. Its real caller is the customer checkout, so the key reaches every customer's
 * browser on the order page. AGOS's copy is `@RequireAuth({"main_admin", "admin"})` and answers 401 without
 * a token — measured 2026-08-11. This page reads `GET /api/v1/loyalty/settings` — `{main_admin, admin}` —
 * and takes the
 * nine fields it needs **by name**, so the credential never enters page state. The page says the key is
 * present without showing it. Filed for the backend; nothing here quotes it.
 *
 * **FB-139.** `isConfigured = !!fromEmail.trim()` decided a banner reading *systémové emaily sa odosielajú
 * z …*. Sending takes three things — an address, the service key, and `is_email_enabled` — and the banner
 * checked one of them.
 *
 * **FB-140.** The master switch says *(transakčné OK)*. `OrderConfirmationMail` requires
 * `order_confirmation_enabled` **and** `is_email_enabled`, so switching it off stops the order confirmation:
 * the one transactional message there is.
 *
 * **And the two templates use different placeholder syntaxes** — `{{name}}` for the welcome body,
 * `{meno}` for the points body — six rows apart, each labelled correctly and neither saying so. An
 * unrecognised placeholder is left standing by both renderers, so the mistake reaches the customer.
 *
 * Ported from legacy `pages/settings-email.tsx` (351 lines).
 */

export function SettingsEmailPage() {
  const { data, isLoading, isError, refetch } = useGetLoyaltySettings()

  /* §9.617 - the editor is declined rather than its two saves disabled, and the guard sits here because this
   * is where the read is: `{ ...data }` spreads `undefined` into `{}`, so `EmailEditor` needed no change at
   * all once the absent payload cannot reach it.
   *
   * **What a blank bag claimed.** `sendingState` answers `no-sender`, so the banner printed *Bez odosielacej
   * adresy neodíde žiadny e-mail - ani overenie registrácie, ani vernostný* - **amber, with a warning
   * triangle**, because the banner is `sending ? emerald : amber`. An outage diagnosis, styled as a fault, on
   * a franchise whose e-mail works. And both saves were offered: `templatePayload` sends
   * `isEmailEnabled: false` plus every customer-facing template as a blank string, and *Uložiť*'s
   * `senderShape` gate never fires because `emailProblem('')` is null by design - the same *a franchise fills
   * these in one at a time* reasoning as `isValidIban('')`. The server binds a present blank straight through.
   *
   * **On `isError`, never on a blank payload.** A franchise that has not set e-mail up yet has blanks in that
   * row legitimately, so refusing on blankness would lock out exactly the people who need this screen. */
  if (isError) {
    return (
      <div className="space-y-4">
        <ReadFailedBlock
          marker="read"
          of="settings-email"
          title={tt('Nastavenia e-mailov sa nepodarilo načítať')}
          onRetry={() => void refetch()}
        >
          <T>
            Preto tu nie je ani formulár - prázdne polia by sa dali uložiť a prepísať odosielaciu adresu aj
            texty e-mailov, ktoré chodia zákazníkom.
          </T>
        </ReadFailedBlock>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  // Deliberately kept as a bag rather than a named shape: `formOf` picks nine keys out of it, and typing
  // the tenth would put the credential's name in a declaration this module has no business holding.
  // Spread, not passed: the prop stays a bag on purpose (see above), and `{ ...data }` widens the
  // typed payload into one without this module naming the credential it is avoiding.
  return <EmailEditor settings={{ ...data }} />
}

const FIELD =
  'px-3 py-2 rounded-lg bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60'

function EmailEditor({ settings }: { settings: Record<string, unknown> | undefined }) {
  const { toast } = useToast()
  const { user, hasPermission } = useAuth()
  const queryClient = useQueryClient()

  const [form, setForm] = useState<EmailForm>(() => formOf(settings))
  const [testTarget, setTestTarget] = useState(() => formOf(settings).fromEmail)
  const [confirmTest, setConfirmTest] = useState(false)

  /* §9.758, BACKEND.md §10A.413 - the sender, the templates, the test e-mail and the e-mail wording all ask
     `settings_email` now, so an administrator holding it stores what they open this page to change. */
  const writable = mayWrite('email', user?.systemRole, hasPermission)
  const readOnly = readOnlyReason('email', user?.systemRole, hasPermission)
  const hasKey = carriesCredential(settings)
  const state = sendingState(form, hasKey)
  const channels = channelsOf(form, hasKey)

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: getGetLoyaltySettingsQueryKey() })

  // §9.395 - the error, which this took no parameter for. The sentence below is the fallback for a
  // refusal with an empty body; the server's own words come first, as they do forty lines down.
  const failed = (caught: unknown) =>
    toast({
      variant: 'destructive',
      title: tt('Uloženie zlyhalo'),
      description: apiErrorMessage(caught, tt('Na serveri sa nič nezmenilo.')),
    })

  const saveSender = usePutLoyaltySettings({
    mutation: {
      onSuccess: () => {
        invalidate()
        toast({ title: tt('Odosielateľ uložený') })
      },
      onError: failed,
    },
  })

  const saveTemplates = usePutLoyaltySettings({
    mutation: {
      onSuccess: () => {
        invalidate()
        toast({ title: tt('Šablóny uložené') })
      },
      onError: failed,
    },
  })

  const sendTest = usePostSettingsLoyaltyTestEmail({
    mutation: {
      onSuccess: () =>
        toast({
          title: tt('Testovací e-mail odoslaný'),
          description: `${tt('Odišel na')} ${testTarget.trim()}`,
        }),
      onError: (error: unknown) =>
        toast({
          variant: 'destructive',
          title: tt('Testovací e-mail neodišel'),
          description: apiErrorMessage(error, tt('Odosielacia služba ho odmietla alebo je nedostupná.')),
        }),
    },
  })

  const set = <K extends keyof EmailForm>(key: K, value: EmailForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const senderShape = emailProblem(form.fromEmail)
  const targetShape = emailProblem(testTarget)

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Nastavenia e-mailu')}
        description={tt('Odkiaľ chodia systémové e-maily a čo v nich je')}
        icon={<Mail className="w-5 h-5" />}
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {readOnly}
        </p>
      )}

      {/* FB-139. Three conditions, and legacy checked one. */}
      <div
        className={cn(
          'rounded-2xl border px-4 py-3 flex items-start gap-3',
          state === 'sending'
            ? 'border-emerald-500/40 bg-emerald-500/5'
            : 'border-amber-500/40 bg-amber-500/5',
        )}
        data-sending={state}
      >
        {state === 'sending' ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0">
          <p
            className={cn(
              'text-sm font-semibold',
              state === 'sending' ? 'text-emerald-500' : 'text-amber-500',
            )}
          >
            {state === 'sending' ? (
              <>
                <T>E-maily odchádzajú z</T> <code className="text-xs">{form.fromEmail}</code>
              </>
            ) : (
              <T>E-maily teraz neodchádzajú</T>
            )}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{sendingMessage(state)}</p>
          <ul className="text-xs text-muted-foreground mt-2 space-y-0.5">
            <ChannelLine live={channels.welcome} label={tt('Uvítací e-mail po registrácii')} id="welcome" />
            <ChannelLine
              live={channels.orderConfirmation}
              label={tt('Potvrdenie objednávky')}
              id="orderConfirmation"
            />
            <ChannelLine live={channels.points} label={tt('Notifikácia o bodoch')} id="points" />
          </ul>
        </div>
      </div>

      {/* FB-138. Said, never shown. */}
      <p
        className="text-[11px] text-muted-foreground flex items-center gap-1.5"
        data-credential={hasKey ? 'present' : 'absent'}
      >
        <KeyRound className="w-3 h-3 shrink-0" />
        {hasKey ? (
          <T>
            Kľúč k odosielacej službe je nastavený. Táto obrazovka ho nezobrazuje ani neposiela - mení sa v
            centrále.
          </T>
        ) : (
          <T>Kľúč k odosielacej službe nie je nastavený, takže neodíde žiadny e-mail.</T>
        )}
      </p>

      <Section title={tt('Odosielateľ')} icon={<Mail className="w-4 h-4 text-primary" />}>
        <Row
          label={tt('E-mailová adresa odosielateľa')}
          desc={tt('Adresa, z ktorej e-maily odchádzajú. Jej doména musí byť overená v odosielacej službe.')}
        >
          <input
            type="email"
            aria-label={tt('E-mailová adresa odosielateľa')}
            className={cn(FIELD, 'w-64', senderShape !== null && 'border-red-500')}
            disabled={!writable}
            placeholder="noreply@pizzamajstro.sk"
            value={form.fromEmail}
            onChange={(event) => set('fromEmail', event.target.value)}
          />
          {senderShape !== null && (
            <p className="text-[11px] text-red-500" data-sender-problem>
              <T>Toto nie je e-mailová adresa.</T>
            </p>
          )}
        </Row>
        <Row label={tt('Meno odosielateľa')} desc={tt('Zobrazí sa príjemcovi ako meno odosielateľa.')}>
          <input
            type="text"
            aria-label={tt('Meno odosielateľa')}
            className={cn(FIELD, 'w-64')}
            disabled={!writable}
            placeholder={tt('Názov vašej reštaurácie')}
            value={form.fromName}
            onChange={(event) => set('fromName', event.target.value)}
          />
        </Row>
        <div className="px-5 py-4 flex justify-end">
          <button
            type="button"
            data-save-sender
            disabled={!writable || saveSender.isPending || senderShape !== null}
            onClick={() => saveSender.mutate({ data: { ...senderPayload(form) } })}
            className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {saveSender.isPending ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť</T>
          </button>
        </div>
      </Section>

      <Section title={tt('Testovací e-mail')} icon={<Send className="w-4 h-4 text-primary" />}>
        <Row
          label={tt('Odoslať testovací e-mail')}
          desc={tt('Odíde skutočný e-mail. Test obchádza hlavný vypínač, takže funguje aj keď je vypnutý.')}
        >
          <div className="flex gap-2">
            <input
              type="email"
              aria-label={tt('Adresa pre test')}
              className={cn(FIELD, 'w-52', targetShape !== null && 'border-red-500')}
              disabled={!writable}
              placeholder="vas@email.sk"
              value={testTarget}
              onChange={(event) => setTestTarget(event.target.value)}
            />
            <button
              type="button"
              data-send-test
              disabled={!writable || sendTest.isPending || !mayTest(form, testTarget, hasKey)}
              onClick={() => setConfirmTest(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border bg-muted text-sm font-medium disabled:opacity-50"
            >
              {sendTest.isPending ? <Spinner /> : <Send className="w-4 h-4" />} <T>Odoslať</T>
            </button>
          </div>
        </Row>
      </Section>

      <Section title={tt('Automatické e-maily')} icon={<FileText className="w-4 h-4 text-primary" />}>
        <Toggle
          label={tt('Hlavný vypínač automatických e-mailov')}
          // FB-140. Legacy's own description promised the opposite.
          desc={tt('Keď je vypnutý, neodíde ani potvrdenie objednávky - vypína všetky tri e-maily nižšie.')}
          value={form.isEmailEnabled}
          disabled={!writable}
          onToggle={() => set('isEmailEnabled', !form.isEmailEnabled)}
          testId="isEmailEnabled"
        />
      </Section>

      <Section title={tt('Uvítací e-mail po registrácii')} icon={<Heart className="w-4 h-4 text-primary" />}>
        <TemplateRows
          kind="welcome"
          subject={form.welcomeEmailSubject}
          body={form.welcomeEmailBody}
          disabled={!writable}
          onSubject={(value) => set('welcomeEmailSubject', value)}
          onBody={(value) => set('welcomeEmailBody', value)}
        />
      </Section>

      <Section title={tt('Potvrdenie objednávky')} icon={<Receipt className="w-4 h-4 text-primary" />}>
        <Toggle
          label={tt('Poslať e-mail pri každej novej objednávke')}
          desc={tt('Zákazník dostane zhrnutie objednávky, položky a sumu. Vyžaduje aj hlavný vypínač.')}
          value={form.orderConfirmationEnabled}
          disabled={!writable}
          onToggle={() => set('orderConfirmationEnabled', !form.orderConfirmationEnabled)}
          testId="orderConfirmationEnabled"
        />
      </Section>

      <Section title={tt('Notifikácia o získaných bodoch')} icon={<Star className="w-4 h-4 text-primary" />}>
        <Toggle
          label={tt('Poslať e-mail pri pripísaní bodov')}
          desc={tt('Zákazník dostane informáciu o novom zostatku bodov.')}
          value={form.pointsEmailEnabled}
          disabled={!writable}
          onToggle={() => set('pointsEmailEnabled', !form.pointsEmailEnabled)}
          testId="pointsEmailEnabled"
        />
        {form.pointsEmailEnabled && (
          <TemplateRows
            kind="points"
            subject={form.pointsEmailSubject}
            body={form.pointsEmailTemplate}
            disabled={!writable}
            onSubject={(value) => set('pointsEmailSubject', value)}
            onBody={(value) => set('pointsEmailTemplate', value)}
          />
        )}
        <div className="px-5 py-4 flex justify-end">
          <button
            type="button"
            data-save-templates
            disabled={!writable || saveTemplates.isPending}
            onClick={() => saveTemplates.mutate({ data: { ...templatePayload(form) } })}
            className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {saveTemplates.isPending ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť šablóny</T>
          </button>
        </div>
      </Section>

      {/* §9.322, the owner's Q11 - *"Make every email ETC we are using, to be configurable"*. The
          three sections above read `loyalty_settings`; this one reads `notification_templates` and
          covers the messages that had no editor at all. Its own store, so a save of one cannot
          revert the other. */}
      <EmailMessageEditor writable={writable} />

      <Section title={tt('O odosielacej službe')} icon={<Info className="w-4 h-4 text-primary" />}>
        <div className="px-5 py-4 space-y-2 text-xs text-muted-foreground">
          <p>
            <T>E-maily odchádzajú cez službu</T> <strong className="text-foreground">Resend</strong>.
          </p>
          <p>
            {/* §9.322 - this sentence used to list five messages and the page offered a box for
                two of them. Every one of them is editable now, and this says where. */}
            <T>Každý e-mail, ktorý systém posiela, sa dá prepísať:</T>{' '}
            <T>uvítací e-mail</T>, <T>notifikácia o bodoch</T> <T>a</T>{' '}
            <T>potvrdenie objednávky, obnovenie hesla a overenie registrácie v sekcii Texty
            e-mailov</T>. <T>Text marketingovej kampane sa píše pri každej kampani zvlášť.</T>
          </p>
          <p>
            {/* The domain check is somebody else's screen; naming it is all this page can honestly do. */}
            <T>
              Doména odosielacej adresy musí byť v službe overená. Bez overenia e-maily odídu a nedoručia sa,
              a táto obrazovka to nevie zistiť.
            </T>
          </p>
        </div>
      </Section>

      <ConfirmDialog
        open={confirmTest}
        onClose={() => setConfirmTest(false)}
        title={tt('Odoslať skutočný testovací e-mail?')}
        description={`${tt('Odíde na')} ${testTarget.trim()}. ${tt('Test obchádza hlavný vypínač.')}`}
        confirmLabel={tt('Odoslať')}
        icon={<Send className="w-5 h-5" />}
        loading={sendTest.isPending}
        onConfirm={() => {
          setConfirmTest(false)
          sendTest.mutate({ data: { to: testTarget.trim() } })
        }}
      />
    </div>
  )
}

function ChannelLine({ live, label, id }: { live: boolean; label: string; id: string }) {
  return (
    <li className="flex items-center gap-1.5" data-channel-state={live ? 'on' : 'off'} data-channel-id={id}>
      <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', live ? 'bg-emerald-500' : 'bg-zinc-500')} />
      {label} - {live ? <T>odíde</T> : <T>neodíde</T>}
    </li>
  )
}

function TemplateRows({
  kind,
  subject,
  body,
  disabled,
  onSubject,
  onBody,
}: {
  kind: TemplateKind
  subject: string
  body: string
  disabled: boolean
  onSubject: (value: string) => void
  onBody: (value: string) => void
}) {
  const problems = templateProblems(kind, body)
  const subjectProblems = templateProblems(kind, subject)
  // §9.157 — `disabled` is this panel's word for "the e-mail is switched off", so an enabled panel
  // is an enabled e-mail. Rendered above the fields rather than beside one, because an empty
  // subject and an empty body are one problem with the message, not two problems with two inputs.
  const empty = emptyMessageProblem(!disabled, subject, body)
  return (
    <>
      {empty !== null && (
        <p className="text-xs text-amber-500 px-1 pb-1" data-empty-template={kind}>
          {templateProblemMessage(kind, empty)}
        </p>
      )}
      <Row label={tt('Predmet')} desc={`${tt('Premenné')}: ${placeholderExample(kind)}`}>
        <input
          type="text"
          aria-label={`${tt('Predmet')} - ${kind}`}
          className={cn(FIELD, 'w-72')}
          disabled={disabled}
          value={subject}
          onChange={(event) => onSubject(event.target.value)}
        />
        {subjectProblems.map((problem) => (
          <p
            key={problem.kind}
            className="text-[11px] text-amber-500"
            data-template-problem={`${kind}-subject-${problem.kind}`}
          >
            {templateProblemMessage(kind, problem)}
          </p>
        ))}
      </Row>
      <Row
        label={tt('Telo')}
        desc={`${tt('HTML je povolené. Premenné')}: ${placeholderExample(kind)}`}
      >
        <textarea
          aria-label={`${tt('Telo')} - ${kind}`}
          rows={6}
          className={cn(FIELD, 'w-72 font-mono resize-y')}
          disabled={disabled}
          value={body}
          onChange={(event) => onBody(event.target.value)}
        />
        {problems.map((problem) => (
          <p
            key={problem.kind}
            className="text-[11px] text-amber-500"
            data-template-problem={`${kind}-body-${problem.kind}`}
          >
            {templateProblemMessage(kind, problem)}
          </p>
        ))}
      </Row>
    </>
  )
}

function Section({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        {icon}
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{title}</h2>
      </div>
      <div className="divide-y divide-border">{children}</div>
    </div>
  )
}

function Row({
  label,
  desc,
  children,
}: {
  label: string
  desc: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4 gap-4">
      <div className="flex-1 pt-1 min-w-0">
        <p className="font-semibold text-foreground text-sm">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <div className="shrink-0 space-y-1">{children}</div>
    </div>
  )
}

function Toggle({
  label,
  desc,
  value,
  disabled,
  onToggle,
  testId,
}: {
  label: string
  desc: string
  value: boolean
  disabled: boolean
  onToggle: () => void
  testId: string
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4 gap-4" data-toggle={testId}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-foreground text-sm">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        disabled={disabled}
        data-toggle-state={value ? 'on' : 'off'}
        onClick={onToggle}
        className={cn(
          'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
          value ? 'bg-primary' : 'bg-muted',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
            value ? 'left-[22px]' : 'left-0.5',
          )}
        />
      </button>
    </div>
  )
}
